import { Children, cloneElement, isValidElement } from 'react';
import type { JSX, ReactElement, ReactNode } from 'react';
import styles from './Field.module.css';

export interface FieldProps {
  label: ReactNode;
  htmlFor: string;
  error?: ReactNode;
  children: ReactNode;
  /** Classe ajoutée au conteneur, pour un champ au gabarit particulier (code 2FA). */
  className?: string;
}

/**
 * Champ de formulaire étiqueté, avec message d'erreur optionnel sous le
 * contrôle. En erreur, le contrôle (l'enfant dont l'`id` est `htmlFor`) reçoit
 * `aria-invalid` et `aria-describedby` : le lecteur d'écran annonce le message
 * avec le champ, et le champ prend son liseré d'erreur.
 */
export function Field({ label, htmlFor, error, children, className }: FieldProps): JSX.Element {
  const idErreur = `${htmlFor}-erreur`;
  // Toujours via `Children.map` : changer de forme quand l'erreur apparaît
  // ferait remonter le contrôle et lui ferait perdre le focus en pleine saisie.
  const contenu = Children.map(children, (enfant) => {
    if (!error || !isValidElement(enfant)) return enfant;
    const element = enfant as ReactElement<{ id?: string; 'aria-describedby'?: string }>;
    if (element.props.id !== htmlFor) return enfant;
    return cloneElement(element, {
      'aria-invalid': true,
      'aria-describedby': [element.props['aria-describedby'], idErreur].filter(Boolean).join(' '),
    } as Record<string, unknown>);
  });
  return (
    <div className={[styles.champ, className].filter(Boolean).join(' ')}>
      <label className={styles.etiquette} htmlFor={htmlFor}>
        {label}
      </label>
      {contenu}
      {error ? (
        <p id={idErreur} className={styles.erreur} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
